import { useState, useEffect } from "react";
import { STAGES, STAGE_ORDER, SOURCING_STAGES, isOutreachDeal } from "../../constants/dealStages";
import { supabase } from "../../supabase";
import DealCard from "./DealCard";
import PipelineTable from "./PipelineTable";
import PipelineInsights from "./PipelineInsights";
import { Download } from "lucide-react";
import { exportPipelineReport } from "../../services/pipelineExport";

export default function PipelineDashboard({ deals, loading, onSelectDeal, photoUrls = {}, onStageChange, onAddDeal, onAddDealFromOM, readOnly = false }) {
  // ── Filter state ───────────────────────────────────────────────────────────
  const [filterMarket, setFilterMarket] = useState("All");
  const [filterStages, setFilterStages] = useState(new Set());
  const [hidePassed,   setHidePassed]   = useState(true); // on by default
  const [minSF,        setMinSF]        = useState("");
  const [maxSF,        setMaxSF]        = useState("");
  const [minPrice,     setMinPrice]     = useState("");
  const [maxPrice,     setMaxPrice]     = useState("");
  const [searchTerm,   setSearchTerm]   = useState("");
  // Within-stage sort: "default" preserves the existing order (typically
  // created-at desc), "cfo_asc" puts the soonest CFO date first, "cfo_desc"
  // the furthest. Deals without a CFO date always sink to the bottom.
  const [sortBy,       setSortBy]       = useState("default");
  // Table by default. The cards group by stage, which makes stage the
  // primary cut whether or not that is the question being asked; the
  // table makes it a column like any other and fits fifteen deals on a
  // screen where three cards did. The cards stay available.
  const [layout,       setLayout]       = useState(
    () => localStorage.getItem("pp.pipelineLayout") || "table");
  useEffect(() => { localStorage.setItem("pp.pipelineLayout", layout); }, [layout]);

  // Export pipeline: the branded LP update (rolling 30 days + active deals),
  // printed to PDF. See services/pipelineExport.js.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  async function handleExport() {
    setExporting(true); setExportError("");
    try { await exportPipelineReport(); }
    catch (err) { setExportError(err.message || "Export failed"); }
    finally { setExporting(false); }
  }

  // MSA filter dropdown: load the active MSAs from msa_definitions so the
  // options stay in sync with what Settings shows.
  const [msaOptions, setMsaOptions] = useState([]);
  useEffect(() => {
    supabase
      .from("msa_definitions")
      .select("name")
      .eq("active", true)
      .order("display_order")
      .order("name")
      .then(({ data }) => setMsaOptions((data || []).map(r => r.name)));
  }, []);
  // Fallback: also union any MSA names present on deals but not (yet) in
  // msa_definitions — keeps the dropdown correct even if the two lists drift.
  const usedMarkets = (() => {
    const set = new Set(msaOptions);
    for (const d of deals) for (const m of (d.msas || [])) if (m) set.add(m);
    return [...set].sort();
  })();

  // Sort by stage priority, preserving existing order within each stage
  const sorted = [...deals].sort((a, b) =>
    (STAGE_ORDER[a.stage] ?? 99) - (STAGE_ORDER[b.stage] ?? 99)
  );

  // Apply filters (AND logic)
  const filtered = sorted.filter((deal) => {
    // Outreach deals belong to the Sourcing flow, not the Pipeline view —
    // these are LOIs the Outreach tab fired, not deals we are actively
    // working. ("Unsolicited Offer" is the retired name for the same thing;
    // kept here so any un-migrated row behaves identically.)
    // Tracking is passive data-warehouse only (properties we've ingested but
    // are not actively working). All are suppressed unconditionally here so
    // the stage groups stay focused on active deals.
    if (SOURCING_STAGES.includes(deal.stage)) return false;
    // Broker LOIs live in the Outreach section until they move into Pipeline.
    if (isOutreachDeal(deal)) return false;
    if (deal.stage === "Tracking") return false;
    if (filterMarket !== "All") {
      // Deal matches if the selected MSA is anywhere in the deal's msas[]
      // array. Falls back to the old free-text string match for legacy rows
      // that haven't been retagged.
      const inMsas = (deal.msas || []).includes(filterMarket);
      const inLegacyString = (deal.market || "").split(",").map(s => s.trim()).includes(filterMarket);
      if (!inMsas && !inLegacyString) return false;
    }
    if (filterStages.size > 0 && !filterStages.has(deal.stage)) return false;
    if (hidePassed && (deal.stage === "Passed" || deal.stage === "Lost" || deal.stage === "Dead")) return false;
    if (minSF    && deal.squareFeet  && deal.squareFeet  < Number(minSF))    return false;
    if (maxSF    && deal.squareFeet  && deal.squareFeet  > Number(maxSF))    return false;
    if (minPrice && deal.askingPrice && deal.askingPrice < Number(minPrice)) return false;
    if (maxPrice && deal.askingPrice && deal.askingPrice > Number(maxPrice)) return false;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const nameMatch = deal.sourceNotes?.match(/^Name:\s*(.+)$/m);
      const dealName = nameMatch ? nameMatch[1].toLowerCase() : "";
      const addrs = (deal.addresses || []).join(" ").toLowerCase();
      const market = (deal.market || "").toLowerCase();
      if (!dealName.includes(q) && !addrs.includes(q) && !market.includes(q)) return false;
    }
    return true;
  });

  // Group filtered deals by stage: the live pipeline (In Diligence first), then
  // closed deals (Acquired), then the terminal stages (Dead last)
  const stagesByPriority = ["In Diligence", "LOI Submitted", "Underwriting", "Screening", "Acquired", "Passed", "Lost", "Dead"];
  const groups = stagesByPriority
    .map((stage) => {
      let groupDeals = filtered.filter((d) => d.stage === stage);
      if (sortBy === "cfo_asc" || sortBy === "cfo_desc") {
        groupDeals = [...groupDeals].sort((a, b) => {
          // Nulls always sink to the bottom regardless of direction
          if (!a.callForOffersDate && !b.callForOffersDate) return 0;
          if (!a.callForOffersDate) return 1;
          if (!b.callForOffersDate) return -1;
          const cmp = a.callForOffersDate.localeCompare(b.callForOffersDate); // YYYY-MM-DD strings sort lexically
          return sortBy === "cfo_asc" ? cmp : -cmp;
        });
      }
      return { stage, deals: groupDeals };
    })
    .filter((g) => g.deals.length > 0);

  const hasActiveFilters =
    filterMarket !== "All" || filterStages.size > 0 || !hidePassed ||
    minSF !== "" || maxSF !== "" || minPrice !== "" || maxPrice !== "" || searchTerm !== "";

  function clearFilters() {
    setFilterMarket("All"); setFilterStages(new Set()); setHidePassed(true);
    setMinSF(""); setMaxSF(""); setMinPrice(""); setMaxPrice(""); setSearchTerm("");
  }

  const filterInput = "bg-[#2c2c2c] border border-[#3a3a3a] text-[#b4b4b4] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#e93529] transition-colors";

  return (
    <>
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pipeline header */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-[#f4f4f4]">Pipeline</h2>
          {!loading && (() => {
            // Exclude Unsolicited Offers and Tracking from the total too —
            // they don't belong on this view and shouldn't inflate the
            // denominator.
            const pipelineTotal = deals.filter(d => !SOURCING_STAGES.includes(d.stage) && d.stage !== "Tracking" && !isOutreachDeal(d)).length;
            return (
              <span className="text-sm text-[#8a8a8a]">
                {filtered.length !== pipelineTotal
                  ? `${filtered.length} of ${pipelineTotal} deals`
                  : `${pipelineTotal} deals`}
              </span>
            );
          })()}
          {!readOnly && (
            <button onClick={handleExport} disabled={exporting}
              className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold transition-colors border border-[#3a3a3a] bg-[#242424] text-[#b4b4b4] hover:text-[#f4f4f4] hover:bg-[#2c2c2c] disabled:opacity-60 disabled:cursor-wait">
              <Download className="w-3.5 h-3.5" />
              {exporting ? "Preparing…" : "Export pipeline"}
            </button>
          )}
          {exportError && <span className="basis-full text-right text-[12px] text-[#f04f44]">{exportError}</span>}
        </div>

        {/* The analytical band: the funnel, the flow and where deals die.
            Collapsible, because somebody opening Pipeline to find one deal
            should not have to scroll past charts to reach the table. */}
        {!loading && deals.length > 0 && <PipelineInsights deals={deals.filter((d) => !isOutreachDeal(d))} />}

        {/* Layout toggle — the table answers "compare these" and the cards
            answer "what is in each stage". Both are useful; only one can be
            the default, and the table is the one that shows the new data. */}
        <div className="mb-4 inline-flex rounded-lg bg-[#2c2c2c] border border-[#3a3a3a] p-0.5">
          {[["table", "Table"], ["cards", "Cards"]].map(([k, label]) => (
            <button key={k} onClick={() => setLayout(k)}
              className={`px-3 py-1 rounded-md text-[12px] font-medium transition ${
                layout === k ? "bg-[#3a3a3a] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#f4f4f4]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Filter bar ── */}
        {!loading && deals.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2 p-3 bg-[#242424] border border-[#3a3a3a] rounded-lg">
            <input
              type="text"
              placeholder="Search deal name or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={filterInput + " w-56"}
            />
            <select value={filterMarket} onChange={(e) => setFilterMarket(e.target.value)} className={filterInput}>
              <option value="All">All Markets</option>
              {usedMarkets.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>

            <div className="relative group">
              <button className={filterInput + " flex items-center gap-1 cursor-pointer"}>
                {filterStages.size === 0 ? "All Stages" : `${filterStages.size} Stage${filterStages.size > 1 ? "s" : ""}`}
                <span className="text-[#8a8a8a] text-xs">▾</span>
              </button>
              <div className="absolute top-full left-0 mt-1 z-20 bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 min-w-[150px] hidden group-hover:block">
                {STAGES.filter((s) => !SOURCING_STAGES.includes(s)).map(s => (
                  <label key={s} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[#3a3a3a] transition-colors cursor-pointer" style={{ color: filterStages.has(s) ? '#e93529' : undefined }}>
                    <input type="checkbox" checked={filterStages.has(s)} onChange={() => setFilterStages(prev => {
                      const next = new Set(prev);
                      if (next.has(s)) next.delete(s); else next.add(s);
                      return next;
                    })} className="accent-pp-red w-3.5 h-3.5" />
                    {s}
                  </label>
                ))}
                {filterStages.size > 0 && (
                  <button onClick={() => setFilterStages(new Set())} className="w-full text-left px-3 py-1.5 text-xs text-pp-red hover:bg-[#3a3a3a] transition-colors border-t border-[#3a3a3a] mt-1">
                    Clear Stages
                  </button>
                )}
              </div>
            </div>

            <label className="flex items-center gap-1.5 text-xs text-[#8a8a8a] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hidePassed}
                onChange={(e) => setHidePassed(e.target.checked)}
                className="accent-pp-red"
              />
              Hide Passed/Lost/Dead
            </label>

            <div className="flex items-center gap-1 text-xs text-[#8a8a8a]">
              <span>Sort:</span>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={filterInput} title="Sort within each stage">
                <option value="default">Default</option>
                <option value="cfo_asc">CFO date — soonest first</option>
                <option value="cfo_desc">CFO date — furthest first</option>
              </select>
            </div>

            <div className="flex items-center gap-1 text-xs text-[#8a8a8a]">
              <span>SF:</span>
              <input type="number" placeholder="Min" value={minSF} onChange={(e) => setMinSF(e.target.value)} className={filterInput + " w-20"} />
              <span>–</span>
              <input type="number" placeholder="Max" value={maxSF} onChange={(e) => setMaxSF(e.target.value)} className={filterInput + " w-20"} />
            </div>

            <div className="flex items-center gap-1 text-xs text-[#8a8a8a]">
              <span>$:</span>
              <input type="number" placeholder="Min" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className={filterInput + " w-24"} />
              <span>–</span>
              <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className={filterInput + " w-24"} />
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-[#f04f44] hover:text-[#e93529] transition-colors">
                Clear filters
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-24 text-[#8a8a8a]">
            <div className="flex justify-center gap-1.5 mb-4">
              <span className="w-2 h-2 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-[#e93529] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
            <p className="text-sm">Loading pipeline…</p>
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-24 text-[#8a8a8a]">
            <p className="text-5xl mb-4">📂</p>
            <p className="text-lg">No deals in pipeline yet.</p>
            {!readOnly && (
              <div className="mt-4 flex items-center justify-center gap-4">
                <button onClick={onAddDeal} className="text-[#f04f44] hover:text-[#e93529] underline text-sm">Add manually</button>
                <span className="text-[#3a3a3a]">or</span>
                <button onClick={onAddDealFromOM} className="text-[#f04f44] hover:text-[#e93529] underline text-sm">Upload an OM</button>
              </div>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#8a8a8a]">
            <p className="text-3xl mb-3">🔍</p>
            <p className="text-sm">No deals match your filters.</p>
            <button onClick={clearFilters} className="mt-3 text-[#f04f44] hover:text-[#e93529] underline text-sm">Clear filters</button>
          </div>
        ) : layout === "table" ? (
          // ── One row per deal. Stage is a column, not the primary cut. ──
          <PipelineTable deals={filtered} onSelectDeal={onSelectDeal} />
        ) : (
          // ── Grouped by stage ──
          <div className="flex flex-col gap-8">
            {groups.map(({ stage, deals: groupDeals }) => (
              <div key={stage}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#8a8a8a]">{stage}</span>
                  <div className="flex-1 h-px bg-[#3a3a3a]" />
                  <span className="text-xs text-[#7a7a7a]">{groupDeals.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                  {groupDeals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      onClick={() => onSelectDeal(deal)}
                      photoUrl={photoUrls[deal.id]}
                      onStageChange={onStageChange}
                      readOnly={readOnly}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
}

// ─── Add Deal Form ────────────────────────────────────────────────────────────

